import {
    CUSTOM_VOICE_VALUE,
    type SelectableVoice,
} from "~/features/meditations/types";

export interface MeditationVoiceOption {
    value: SelectableVoice;
    label: string;
    /** Compact label for tile pickers ("You", "Laura"); catalog voices omit it. */
    shortLabel?: string;
    kind: "catalog" | "custom";
    /** Owning practitioner, for custom voices only. */
    practitionerId?: string;
    ownedBySelf?: boolean;
}

// AIDEV-NOTE: Voice memory is context-scoped so DPMP can retain its own default.
export type MeditationVoiceContext = "general" | "dpmp";

interface ResolveVoiceOptionsInput {
    catalog: MeditationVoiceOption[];
    custom: MeditationVoiceOption[];
    storedVoice: string | null;
    context: MeditationVoiceContext;
    /**
     * An INHERITED practitioner voice the client hasn't been shown yet — i.e. their
     * practitioner has just generated and gone live with one. It takes the default a
     * single time, then normal precedence resumes. Null in every other case.
     */
    newlyInheritedVoice?: MeditationVoiceOption | null;
}

interface ResolvedVoiceOptions {
    options: MeditationVoiceOption[];
    defaultVoice: SelectableVoice;
}

// AIDEV-NOTE: Inherited voices stay after the catalog; only an unoverridden owned
// clone leads and becomes the default.
//
// The ONE exception is newlyInheritedVoice: when a client's practitioner first goes
// live with a voice, it takes the default once so the client actually hears it rather
// than having to discover it in the picker. It outranks a stored pick deliberately —
// that is the whole point of the switch — but only for that first resolution, so a
// client who then chooses a catalog voice keeps it. Applies to CLIENTS only; a
// practitioner's own clone follows the ordinary ownedCustom path below.
export function resolveMeditationVoiceOptions({
    catalog,
    custom,
    storedVoice,
    context,
    newlyInheritedVoice,
}: ResolveVoiceOptionsInput): ResolvedVoiceOptions {
    const candidates = [...catalog, ...custom];
    const storedOption =
        candidates.find((option) => option.value === storedVoice) ??
        (storedVoice === CUSTOM_VOICE_VALUE ? custom[0] : undefined);
    const dpmpDefault =
        context === "dpmp"
            ? catalog.find((option) => option.value === "Michelle")
            : undefined;
    const ownedCustom = custom.find((option) => option.ownedBySelf);
    const shouldLeadWithOwnedCustom =
        !!ownedCustom && !storedOption && !dpmpDefault;
    const remainingCustom = ownedCustom
        ? custom.filter((option) => option.value !== ownedCustom.value)
        : custom;
    const options = shouldLeadWithOwnedCustom
        ? [ownedCustom, ...catalog, ...remainingCustom]
        : [...catalog, ...custom];

    return {
        options,
        defaultVoice:
            newlyInheritedVoice?.value ??
            storedOption?.value ??
            dpmpDefault?.value ??
            ownedCustom?.value ??
            "Brad",
    };
}
